import { useCallback, useEffect, useRef } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { useCookieConsent } from 'contexts/CookieConsentContext';

/**
 * Hook to manage tracking consent for analytics services.
 * Automatically enables/disables Pendo and Datadog based on Cookiebot consent.
 * Cookiebot consent is only required when NEXT_PUBLIC_COOKIEBOT_ENABLED is 'true'.
 * If the env var is not set or has any other value, tracking is enabled immediately.
 * @return {Object} Consent state and utility functions for managing tracking consent
 */
export const useTrackingConsent = () => {
  const { consent, isConsentReady, hasStatisticsConsent } = useCookieConsent();
  const previousConsent = useRef<boolean | null>(null);

  // Cookiebot is only enabled when NEXT_PUBLIC_COOKIEBOT_ENABLED is explicitly 'true'
  // If env var is not set or any other value, we bypass consent requirements
  const isCookiebotEnabled =
    typeof process !== 'undefined' &&
    process.env.NEXT_PUBLIC_COOKIEBOT_ENABLED === 'true';
  const shouldTrackImmediately = !isCookiebotEnabled;

  // Effective consent is true if either:
  // 1. NEXT_PUBLIC_COOKIEBOT_ENABLED is not 'true' (track immediately)
  // 2. User has given statistics consent via Cookiebot
  const effectiveStatisticsConsent =
    shouldTrackImmediately || hasStatisticsConsent;
  const effectiveConsentReady = shouldTrackImmediately || isConsentReady;

  const disableDatadog = useCallback(() => {
    // Stop session replay recording
    datadogRum.stopSessionReplayRecording();

    // Clear user data
    datadogRum.clearUser();

    // Remove global context properties
    datadogRum.removeGlobalContextProperty('user_email');
    datadogRum.removeGlobalContextProperty('user_type');
    datadogLogs.removeGlobalContextProperty('user_email');
    datadogLogs.removeGlobalContextProperty('user_type');
  }, []);

  const disablePendo = useCallback(() => {
    if (window.pendo) {
      // Clear visitor data by setting anonymous visitor
      window.pendo.clearSession?.();

      // Update visitor to anonymous
      window.pendo.updateOptions?.({
        visitor: {
          id: 'anonymous-opted-out',
        },
      });
    }
  }, []);

  const enableDatadogReplay = useCallback(() => {
    datadogRum.startSessionReplayRecording();
  }, []);

  useEffect(() => {
    if (!effectiveConsentReady) {
      return;
    }

    const currentConsent = effectiveStatisticsConsent;

    // Only act on consent changes
    if (previousConsent.current === currentConsent) {
      return;
    }

    previousConsent.current = currentConsent;

    if (!currentConsent) {
      // User has opted out of statistics cookies
      disableDatadog();
      disablePendo();
    } else {
      // User has opted in - re-enable session replay
      enableDatadogReplay();
    }
  }, [
    effectiveConsentReady,
    effectiveStatisticsConsent,
    disableDatadog,
    disablePendo,
    enableDatadogReplay,
  ]);

  return {
    consent,
    isConsentReady: effectiveConsentReady,
    hasStatisticsConsent: effectiveStatisticsConsent,
    shouldTrackImmediately,
    disableDatadog,
    disablePendo,
  };
};
